import { Popup as AkPopup, type PopupComponentProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ScrollableContainer from '@databases/layout/ScrollableContainer';
import Z_INDEX from '@databases/layout/z-index';
import { useResizeObserverCallback } from '@databases/react';
import React, {
  type ComponentProps,
  type ComponentType,
  type FunctionComponent,
  Fragment,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type PopperChildrenProps } from 'react-popper';
import styled from 'styled-components';

export type Placement = NonNullable<ComponentProps<typeof AkPopup>['placement']>;

const opposites: Record<string, string> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
  start: 'end',
  auto: 'auto',
  end: 'start',
};

export const REDUCED_POPUP_OFFSET: [number, number] = [0, 4];

/**
 * A copy of getFallbackPlacements() from @atlaskit/dropdown-menu. Can be passed as fallbackPlacement to @atlaskit/popup.
 */
function getFallbackPlacements(placement: Placement): Placement[] | undefined {
  const p = placement.split('-');
  if (!['top', 'bottom'].includes(p[0])) {
    return undefined;
  } else if (p.length === 2 && ['start', 'end'].includes(p[1])) {
    return [
      `${p[0]}`,
      `${p[0]}-${opposites[p[1]]}`,
      `${opposites[p[0]]}-${p[1]}`,
      `${opposites[p[0]]}`,
      `${opposites[p[0]]}-${opposites[p[1]]}`,
      'auto',
    ] as Placement[];
  } else {
    return [
      `${p[0]}-start`,
      `${p[0]}-end`,
      `${opposites[p[0]]}`,
      `${opposites[p[0]]}-start`,
      `${opposites[p[0]]}-end`,
      `auto`,
    ] as Placement[];
  }
}

export const DefaultPopupContentContainer = styled(ScrollableContainer)<{
  width?: number;
}>`
  max-height: calc(100vh - 16px);
  flex-shrink: 0;

  > * {
    // ScrollableContainer InnerContainer
    padding: ${token('space.200', '16px')} ${token('space.300', '24px')};
  }
`;

const roundedPopupStyles = xcss({
  display: 'block',
  boxSizing: 'border-box',
  flex: '1 1 auto',
  backgroundColor: 'elevation.surface.overlay',
  borderRadius: 'radius.large',
  boxShadow: 'elevation.shadow.overlay',
  overflow: 'auto',
  padding: 'space.0',
  outline: 'none',
  zIndex: 'dialog',
});

const noContainerScrollStyles = xcss({
  overflow: 'hidden',
});

const createRoundedPopupContainer = (styles: Parameters<typeof Box>[0]['xcss']) =>
  // eslint-disable-next-line react/display-name
  React.forwardRef<HTMLDivElement, Omit<PopupComponentProps, 'xcss'>>(
    (
      {
        children,
        'data-testid': testId,
        shouldRenderToParent: _shouldRenderToParent,
        shouldFitContainer: _shouldFitContainer,
        isReferenceHidden: _isReferenceHidden,
        ...props
      },
      ref
    ) => (
      <Box xcss={styles} testId={testId} {...props} ref={ref}>
        {children}
      </Box>
    )
  ) as ComponentType<PopupComponentProps>;

const RoundedPopupContainer = createRoundedPopupContainer(roundedPopupStyles);

const RoundedPopupNoScrollContainer = createRoundedPopupContainer([
  roundedPopupStyles,
  noContainerScrollStyles,
]);

export type PopupProps = ComponentProps<typeof AkPopup> & {
  width?: number;
  isTooltip?: boolean;
  contentContainer?: ComponentType | null;
  rounded?: boolean;
  disableContainerScroll?: boolean;
};

/**
 * Like @atlaskit/popup, with the following differences:
 * - Supports the open menu padding
 * - Uses fallback placements and a max height to become scrollable when it's higher than the viewport
 * - Shows a drop shadow when it's scrollable
 * - Detects resizes of its content and automatically repositions the popup
 * - Supports a larger border radius (8px) to the popup container (when rounded is true)
 */
const Popup: FunctionComponent<PopupProps> = ({
  isOpen,
  content,
  placement = 'bottom-start',
  width,
  isTooltip,
  contentContainer = DefaultPopupContentContainer,
  rounded = true,
  disableContainerScroll = false,
  ...props
}) => {
  const fallbackPlacements = useMemo(() => getFallbackPlacements(placement), [placement]);

  const updateRef = useRef<PopperChildrenProps['update']>();

  const [contentRef, setContentRef] = useState<HTMLDivElement | undefined | null>(null);
  useResizeObserverCallback(contentRef, () => {
    updateRef.current?.();
  });

  return (
    <AkPopup
      isOpen={isOpen}
      placement={placement}
      zIndex={Z_INDEX.INLINE_DIALOG}
      fallbackPlacements={fallbackPlacements}
      {...(rounded && {
        popupComponent: disableContainerScroll
          ? RoundedPopupNoScrollContainer
          : RoundedPopupContainer,
      })}
      content={(contentProps) => {
        updateRef.current = contentProps.update;

        const Wrapper =
          isTooltip || contentContainer === null ? Fragment : contentContainer;

        return (
          <Wrapper>
            <div ref={setContentRef} style={{ width }}>
              {content(contentProps)}
            </div>
          </Wrapper>
        );
      }}
      autoFocus={!isTooltip}
      {...props}
    />
  );
};

export default Popup;
